import { Component, OnInit, ViewChild, ElementRef, NgZone, Renderer, ErrorHandler, ChangeDetectorRef } from '@angular/core';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import { IAppState } from '../store/state/IAppState';
import { Observable } from 'rxjs/Observable';
import {
  Router,
  RouterEvent,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  ActivationEnd
} from '@angular/router';

import { IUserContext } from '../../security/store/state/IUserContext';
import * as securityStore from '../../security/store/reducers';
import * as securityActions from '../../security/store/actions';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import { ObservableErrorHandler } from '../services/ObservableErrorHandler';
import { AlertModel } from '../../pillar/alerts/models/AlertModel';
import { AlertType } from '../../pillar/alerts/models/AlertType';
import { AppConfigurationService } from '../services/AppConfigurationService';
import { RootNavigationManager } from '../services/RootNavigationManager';

//TODO: Update to use a region instead of hard coded.
import * as messagesStore from '../../messages/store/reducers';
import { MessagesModel } from '../../messages/models/MessagesModel';
import * as messagesActions from '../../messages/store/actions';
import { PortalService } from '../../portal/portal.service';
import { UserService } from '../../customer/services/UserService';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'Datawarehouse';
  appVersion = '';
  logo = '';
  userContext: Observable<IUserContext>;
  errorHandler: ObservableErrorHandler;
  allAlerts: AlertModel[] = [];
  isAuthenticated: Observable<boolean>;
  navLinks: Array<{ path: string, isActive: boolean, label: string, icon: string, permissions: string[] }> = [];
  broadcastMessage = undefined;
  messagesLoaded = false;
  showHeader: boolean;
  opened: boolean = false;
  private _activeIndex: number;
  private _activeLink: { path: string, isActive: boolean, label: string, icon: string, permissions: string[] };
  navigationTitle: 'Administration';
  isPublic: boolean = false;
  public isPortal = false;

  @ViewChild('spinnerElement')
  spinnerElement: ElementRef;

  constructor(private store: Store<IAppState>,
    private router: Router,
    private currentUserService: CurrentUserService,
    private userService: UserService,
    private ngZone: NgZone,
    private renderer: Renderer,
    errorHandler: ErrorHandler,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfigurationService: AppConfigurationService,
    private rootNavigationManager: RootNavigationManager,
    private _location: Location,
    private _portalService: PortalService) {
    router.events.subscribe((event: RouterEvent) => {
      this._navigationInterceptor(event);
    });
    this.errorHandler = errorHandler as ObservableErrorHandler;
    this.errorHandler.errors$.subscribe(e => {
      if (e.status && e.status === 401) {
        this._hideSpinner();
        this.addAlert(new AlertModel(AlertType.Error, e.error));
      }
    });

    this.appVersion = appConfigurationService.configuration.applicationVersion;
    this.title = appConfigurationService.configuration.title;
    this.logo = appConfigurationService.configuration.logo;
    var titleDescription = appConfigurationService.configuration.titleDescription;
    if (titleDescription) {
      this.title = this.title + ' - ' + titleDescription;
    }

    this.setupMessages();

    this.isPortal = this._portalService.isPortalPath(this._location.path());
    this.showHeader = !this.isPortal;    
  }

  setupMessages() {
    this.store
      .select(messagesStore.getMessages)
      .subscribe((v: MessagesModel) => {
        if (v) {
          var currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);
          if (v.activeBroadcastMessage && v.activeBroadcastMessage.expirationTimestamp > currentDate) {
            this.broadcastMessage = v.activeBroadcastMessage.value;
          } else {
            this.broadcastMessage = undefined;
          }
          this.messagesLoaded = true;
          this._hideSpinner();
        } else {
          this.store.dispatch(new messagesActions.GetMessagesAction());
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  removeAlert(alert: AlertModel) {
    if (alert) {
      this.allAlerts = this.allAlerts.filter(a => a != alert);
      this.changeDetectorRef.detectChanges();
    }
  }

  addAlert(alert: AlertModel) {
    if (alert) {
      this.allAlerts = this.allAlerts.concat([alert]);
      this.changeDetectorRef.detectChanges();

    }
  }

  makeActive(link) {
    if (this._activeLink) {
      this._activeLink.isActive = false;
    }
    this._activeLink = link;
    this._activeIndex = this.navLinks.indexOf(link);
  }

  onHistoryOpened() {
    this.opened = true;
  }

  onHistoryClosed() {
    setTimeout(() => {
      this.opened = false;
    }, 150);
  }

  back() {
    this._location.back();
  }

  ngOnInit(): void {
    this.userContext = this.store.select(securityStore.getUserContext);
    this.isAuthenticated = this.store.select(securityStore.getUserContextIsAuthenticated);

    this.store.select(securityStore.getUserContext)
      .subscribe((uc) => {
        if (this.currentUserService.checkPermissionsLoaded()) {
          this.navLinks = [];
          this.rootNavigationManager.getNavigationActiveRoutes()
            .forEach((route, i) => {
              var link = {
                path: route.path,
                isActive: this.rootNavigationManager.router.url.indexOf('/' + route.path) === 0,
                label: route.data.title,
                icon: route.data.icon,
                permissions: route.data.permissions
              };
              this.navLinks.push(link);
              if (link.isActive) {
                this._activeIndex = i;
                this._activeLink = link;
              }
            });
        }
      });

    this.rootNavigationManager.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        var navigationEnd = event as NavigationEnd;
        this.isPublic = navigationEnd.url === '/public';
        this.navLinks.forEach(l => {
          if (navigationEnd.url.indexOf('/' + l.path) === 0) {
            this.makeActive(l);
          }
        });
      }

      if (event instanceof ActivationEnd) {
        var activationEnd = event as ActivationEnd;
        this.navigationTitle = activationEnd.snapshot.data.title;
      }
    });
  }

  private _navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      if (event['url'].includes('user-account')) {
        this.showHeader = false;
      } else {
        if(this._portalService.isPortalPath(this._location.path())){
          this.isPortal = true;
        }
        this.showHeader = !this.isPortal;
        this._portalService.isPortalPath(this._location.path());
      }

      // We want to run this function outside of Angular's zone to
      // bypass change detection
      this.ngZone.runOutsideAngular(() => {
        // For simplicity we are going to turn opacity on / off
        // you could add/remove a class for more advanced styling
        // and enter/leave animation of the spinner
        this.renderer.setElementStyle(
          this.spinnerElement.nativeElement,
          'opacity',
          '1'
        );
      });
    }
    if (event instanceof NavigationEnd) {
      this._hideSpinner();
    }
    // Set loading state to false in both of the below events to
    // hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this._hideSpinner();
    }
    if (event instanceof NavigationError) {
      this._hideSpinner();
    }
  }

  private _hideSpinner(): void {
    if (this.messagesLoaded) {
      // We wanna run this function outside of Angular's zone to
      // bypass change detection,
      this.ngZone.runOutsideAngular(() => {
        // For simplicity we are going to turn opacity on / off
        // you could add/remove a class for more advanced styling
        // and enter/leave animation of the spinner
        this.renderer.setElementStyle(
          this.spinnerElement.nativeElement,
          'opacity',
          '0'
        );
      });
    }
  }

  private getRouterContainer (): string {
    return `${this._location.path()}#routerContainer`;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/components/app.component.ts